import Sidebar from '@rio-cloud/rio-uikit/Sidebar';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { useGetManEulaStatusQuery } from '../../../../services/accountsApi';
import type { Account } from '../../../../services/schemas/accountsBackendCodec';
import { resetSelectedAccountId } from '../accountsSlice';
import { AccountLifeCycleChange } from '../dialogs/AccountLifeCycleChange';
import { EditAccount } from '../dialogs/editAccount/EditAccount';
import { AccountDetails } from './AccountDetails';
import { getShowAccountSidebar, getSidebarAccount, setShowAccountSidebar } from './accountSidebarSlice';
import { AddUserToAccount } from './adminFunctionality/AddUserToAccount';
import { DeleteAccount } from './adminFunctionality/DeleteAccount';
import { DeleteUserInAccount } from './adminFunctionality/DeleteUserInAccount';
import { useFetchAccountSettingQuery } from '../../../../services/usersApi';
import { useEffect, useRef, useState } from 'react';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

const sidebarLayoutStyle = { minHeight: 0 };
const sidebarContentAreaStyle = { position: 'relative', flex: '1 1 auto', minHeight: 0 } as const;
const sidebarContentStyle = { height: '100%', overflowY: 'auto' } as const;
const sidebarActionsStyle = {
    flexShrink: 0,
    borderTop: '1px solid #d8e0e8',
    backgroundColor: '#ffffff',
    padding: '20px 16px 24px',
};
const topGradientStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '24px',
    background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0) 100%)',
    pointerEvents: 'none',
    zIndex: 1,
} as const;
const bottomGradientStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '24px',
    background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0) 100%)',
    pointerEvents: 'none',
    zIndex: 1,
} as const;

export const AccountsSidebar = () => {
    const dispatch = useAppDispatch();
    const contentRef = useRef<HTMLDivElement | null>(null);
    const contentInnerRef = useRef<HTMLDivElement | null>(null);

    const show = useAppSelector(getShowAccountSidebar);
    const accountInSidebar = useAppSelector(getSidebarAccount);

    const { data: manEulaStatus } = useGetManEulaStatusQuery(
        { accountId: accountInSidebar?.id ?? '' },
        { skip: accountInSidebar === undefined }
    );

    const {
        data: accountSetting,
        error: accountSettingError,
        isError: accountSettingIsError,
    } = useFetchAccountSettingQuery(
        { accountId: accountInSidebar?.id ?? '' },
        { skip: accountInSidebar === undefined }
    );

    const [minAccountAcr, setMinAccountAcr] = useState<string | undefined>(undefined);
    const [showTopGradient, setShowTopGradient] = useState(false);
    const [showBottomGradient, setShowBottomGradient] = useState(false);

    const isFetchBaseQueryError = (err: unknown): err is FetchBaseQueryError =>
        typeof err === 'object' && err !== null && 'status' in (err as Record<string, unknown>);

    useEffect(() => {
        if (!accountInSidebar) {
            setMinAccountAcr(undefined);
            return;
        }

        if (!accountSettingIsError) {
            setMinAccountAcr(accountSetting?.min_account_acr ?? undefined);
            return;
        } else if (isFetchBaseQueryError(accountSettingError)) {
            if (accountSettingError.status === 404) {
                setMinAccountAcr('urn:rio:loa:1');
                return;
            }
        }

        setMinAccountAcr(undefined);
    }, [accountInSidebar, accountSetting, accountSettingError, accountSettingIsError]);

    useEffect(() => {
        const contentElement = contentRef.current;

        if (!contentElement) {
            return;
        }

        const updateGradientVisibility = () => {
            const isScrollable = contentElement.scrollHeight - contentElement.clientHeight > 1;
            const nextShowTopGradient = isScrollable && contentElement.scrollTop > 1;
            const nextShowBottomGradient =
                isScrollable &&
                contentElement.scrollTop + contentElement.clientHeight < contentElement.scrollHeight - 1;

            setShowTopGradient(current => (current === nextShowTopGradient ? current : nextShowTopGradient));
            setShowBottomGradient(current => (current === nextShowBottomGradient ? current : nextShowBottomGradient));
        };

        updateGradientVisibility();
        contentElement.addEventListener('scroll', updateGradientVisibility);
        window.addEventListener('resize', updateGradientVisibility);

        let resizeObserver: ResizeObserver | undefined;

        if (typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(updateGradientVisibility);
            resizeObserver.observe(contentElement);

            if (contentInnerRef.current) {
                resizeObserver.observe(contentInnerRef.current);
            }
        }

        return () => {
            contentElement.removeEventListener('scroll', updateGradientVisibility);
            window.removeEventListener('resize', updateGradientVisibility);
            resizeObserver?.disconnect();
        };
    }, [accountInSidebar, manEulaStatus, minAccountAcr]);

    return (
        <Sidebar
            title={'Account Info'}
            titleClassName={'padding-left-10'}
            closed={!show}
            onClose={() => {
                dispatch(setShowAccountSidebar(false));
                dispatch(resetSelectedAccountId());
            }}
            position={'right'}
            fly={true}
            width={500}
        >
            {accountInSidebar && (
                <div className={'display-flex flex-column height-100pct'} style={sidebarLayoutStyle}>
                    <div style={sidebarContentAreaStyle}>
                        {showTopGradient && (
                            <div data-testid='accounts-sidebar-gradient-top' style={topGradientStyle} />
                        )}
                        <div data-testid='accounts-sidebar-content' ref={contentRef} style={sidebarContentStyle}>
                            <div ref={contentInnerRef}>
                                <AccountDetails
                                    account={accountInSidebar}
                                    eulaStatus={manEulaStatus}
                                    minAccountAcr={minAccountAcr}
                                />
                            </div>
                        </div>
                        {showBottomGradient && (
                            <div data-testid='accounts-sidebar-gradient-bottom' style={bottomGradientStyle} />
                        )}
                    </div>
                    <div data-testid='accounts-sidebar-actions' style={sidebarActionsStyle}>
                        <ActionArea account={accountInSidebar} />
                    </div>
                </div>
            )}
        </Sidebar>
    );
};

const ActionArea = ({ account }: { account: Account }) => (
    <div className={'display-flex flex-nowrap justify-content-center'}>
        <div className={'display-grid gap-20'}>
            <div className={'display-grid gap-25 grid-cols-1'}>
                <EditAccount account={account} />
            </div>
            <div className={'display-grid gap-25 grid-cols-2'}>
                <AccountLifeCycleChange account={account} />
                <DeleteAccount account={account} />
            </div>
            <div className={'display-grid gap-25 grid-cols-2'}>
                <AddUserToAccount account={account} />
                <DeleteUserInAccount account={account} />
            </div>
        </div>
    </div>
);
